import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, Pencil, Plus, Send, Trash2, TriangleAlert } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { adminApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ATTENDANCE_CAUSE_LABELS, type AttendanceCauseCode } from "#shared/fortnox";
import {
  decimalHours,
  formatClock,
  formatDuration,
  localDate,
  lunchMinutes,
  missedCheckout,
  workedMinutes,
} from "#shared/timeclock";
import type { Employee, Shift, VacationRequest } from "#shared/types";
import { VACATION_CAUSE_CODES, VACATION_LABELS, workdaysBetween } from "#shared/vacation";

/** ISO-tidsstämpel → "HH:MM" i lokal tid, för <input type="time">. */
function toTimeInput(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function daysAgo(count: number) {
  const date = new Date();
  date.setDate(date.getDate() - count);
  return localDate(date);
}

type EditDraft = {
  shift: Shift;
  check_in: string;
  lunch_start: string;
  lunch_end: string;
  check_out: string;
  note: string;
};

type AddDraft = {
  employee_id: string;
  work_date: string;
  check_in: string;
  lunch_start: string;
  lunch_end: string;
  check_out: string;
  note: string;
};

type ShiftRow = {
  kind: "shift";
  key: string;
  date: string;
  employee: Employee | undefined;
  shift: Shift;
  /** Stämplade in en tidigare dag men aldrig ut — räknas inte som arbetad tid. */
  missed: boolean;
  worked: number;
  lunch: number;
};

type LeaveRow = {
  kind: "leave";
  key: string;
  date: string;
  employee: Employee | undefined;
  request: VacationRequest;
};

type Row = ShiftRow | LeaveRow;

/** En normal arbetsdag, som utgångspunkt när en dag läggs till i efterhand. */
const NORMAL_DAY = {
  check_in: "08:00",
  lunch_start: "12:00",
  lunch_end: "13:00",
  check_out: "17:00",
} as const;

export function TimeLogPanel() {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(daysAgo(13));
  const [to, setTo] = useState(localDate());
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [addDraft, setAddDraft] = useState<AddDraft | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: adminApi.employees,
  });

  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ["shifts", "range", from, to],
    queryFn: () => adminApi.shifts(from, to),
  });

  // Godkänd ledighet visas som rader bland arbetsdagarna, så att en dag utan
  // stämplingar inte ser bortglömd ut. Listan är liten; klipps mot perioden här.
  const { data: approvedLeave = [] } = useQuery({
    queryKey: ["vacation", "godkand"],
    queryFn: () => adminApi.vacation("godkand"),
  });

  const employeeById = useMemo(() => {
    const map = new Map<string, Employee>();
    for (const employee of employees) map.set(employee.id, employee);
    return map;
  }, [employees]);

  const rows = useMemo<Row[]>(() => {
    const today = localDate();

    const shiftRows: Row[] = shifts.map((shift) => ({
      kind: "shift",
      key: shift.id,
      date: shift.work_date,
      employee: employeeById.get(shift.employee_id),
      shift,
      missed: missedCheckout(shift, today),
      worked: missedCheckout(shift, today) ? 0 : workedMinutes(shift),
      lunch: lunchMinutes(shift),
    }));

    const leaveRows: Row[] = approvedLeave.flatMap((request) => {
      const start = request.start_date > from ? request.start_date : from;
      const end = request.end_date < to ? request.end_date : to;
      return workdaysBetween(start, end).map((date) => ({
        kind: "leave" as const,
        key: `${request.id}:${date}`,
        date,
        employee: employeeById.get(request.employee_id),
        request,
      }));
    });

    // Senaste dagen överst, sedan i namnordning — samma som listan sett ut.
    return [...shiftRows, ...leaveRows].sort(
      (a, b) =>
        b.date.localeCompare(a.date) ||
        (a.employee?.name ?? "").localeCompare(b.employee?.name ?? "", "sv"),
    );
  }, [shifts, approvedLeave, employeeById, from, to]);

  const shiftRows = rows.filter((row): row is ShiftRow => row.kind === "shift");
  const leaveCount = rows.length - shiftRows.length;
  const missedCount = shiftRows.filter((row) => row.missed).length;
  const totalMinutes = shiftRows.reduce((sum, row) => sum + row.worked, 0);

  // Det som knappen faktiskt kommer att skicka: avslutat, oskickat, inom intervallet.
  const sendableCount = shiftRows.filter(
    (row) => row.shift.check_out && !row.shift.exported_at && row.employee?.employee_number,
  ).length;

  // Oskickat över alla datum — så att det som ligger utanför intervallet syns.
  const { data: pending } = useQuery({
    queryKey: ["export", "pending"],
    queryFn: adminApi.pendingExport,
  });
  const pendingOutsideRange =
    pending && pending.shifts.count > 0 && pending.shifts.oldest && pending.shifts.oldest < from;

  const saveEdit = useMutation({
    mutationFn: (value: EditDraft) =>
      adminApi.updateShift(value.shift.id, {
        check_in: value.check_in,
        lunch_start: value.lunch_start,
        lunch_end: value.lunch_end,
        check_out: value.check_out,
        note: value.note,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      setDraft(null);
      toast.success("Tiderna är uppdaterade");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const removeDay = useMutation({
    mutationFn: (id: string) => adminApi.deleteShift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      setDraft(null);
      toast.success("Dagen är borttagen");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const addDay = useMutation({
    mutationFn: (value: AddDraft) => adminApi.createShift(value),
    onSuccess: (shift) => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      setAddDraft(null);
      toast.success(`Dagen ${shift.work_date} är tillagd`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const exportToFortnox = useMutation({
    mutationFn: () => adminApi.exportAttendance(from, to),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["balances"] });
      queryClient.invalidateQueries({ queryKey: ["export", "pending"] });
      if (result.balance_error) {
        toast.warning(`Tiden skickades, men flexsaldot kunde inte läsas: ${result.balance_error}`);
      }
      if (result.failures.length > 0) {
        toast.error(
          `${result.sent.length} skickade, ${result.failures.length} misslyckades: ${result.failures[0].message}`,
        );
      } else {
        toast.success(`${result.sent.length} dag(ar) skickade till Fortnox`);
      }
      if (result.skipped_missing_number.length > 0) {
        toast.warning(
          `Hoppade över (saknar anställningsnummer): ${result.skipped_missing_number.join(", ")}`,
        );
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function downloadCsv() {
    const header = [
      "Anstallningsnr",
      "Namn",
      "Datum",
      "In",
      "LunchBorjan",
      "LunchSlut",
      "Ut",
      "Lunchminuter",
      "ArbetadeTimmar",
      "Orsakskod",
      "Anteckning",
    ];

    const lines = rows.map((row) =>
      (row.kind === "leave"
        ? [
            row.employee?.employee_number ?? "",
            row.employee?.name ?? "",
            row.date,
            "",
            "",
            "",
            "",
            "",
            "",
            VACATION_CAUSE_CODES[row.request.kind],
            VACATION_LABELS[row.request.kind],
          ]
        : [
            row.employee?.employee_number ?? "",
            row.employee?.name ?? "",
            row.shift.work_date,
            formatClock(row.shift.check_in),
            formatClock(row.shift.lunch_start),
            formatClock(row.shift.lunch_end),
            formatClock(row.shift.check_out),
            String(row.lunch),
            // Tomt i stället för en fantasisiffra när utstämplingen saknas.
            row.missed ? "" : decimalHours(row.worked),
            row.employee?.attendance_code ?? "",
            (row.shift.note ?? "").replace(/[\r\n";]/g, " "),
          ]
      )
        .map((cell) => `"${cell}"`)
        .join(";"),
    );

    const csv = [header.join(";"), ...lines].join("\r\n");
    // BOM så att Excel läser å/ä/ö rätt.
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `tidrapport-${from}_${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="surface p-5 sm:p-6">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="from">Från</Label>
          <Input
            id="from"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            className="w-40"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="to">Till</Label>
          <Input
            id="to"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            className="w-40"
          />
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            variant="secondary"
            className="rounded-full"
            disabled={employees.length === 0}
            onClick={() =>
              setAddDraft({
                employee_id: employees[0]?.id ?? "",
                work_date: to,
                ...NORMAL_DAY,
                note: "",
              })
            }
          >
            <Plus className="size-4" /> Lägg till dag
          </Button>
          <Button variant="secondary" className="rounded-full" onClick={downloadCsv}>
            <Download className="size-4" /> CSV
          </Button>
          <Button
            className="rounded-full"
            disabled={exportToFortnox.isPending || sendableCount === 0}
            onClick={() => exportToFortnox.mutate()}
          >
            <Send className="size-4" />
            {exportToFortnox.isPending
              ? "Skickar…"
              : sendableCount === 0
                ? "Inget att skicka"
                : `Skicka ${sendableCount} dag(ar) till Fortnox`}
          </Button>
        </div>
      </div>

      {pendingOutsideRange && (
        // Det här är hela poängen: oskickat som inte syns i intervallet.
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-warning/15 px-4 py-3 text-sm">
          <p className="flex items-center gap-2">
            <TriangleAlert className="size-4 shrink-0 text-warning-foreground" />
            <span>
              <strong>{pending.shifts.count} arbetsdag(ar)</strong> är inte skickade till Fortnox,
              den äldsta från {pending.shifts.oldest}. Knappen skickar bara det valda intervallet.
            </span>
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="rounded-full"
            onClick={() => setFrom(pending.shifts.oldest!)}
          >
            Visa allt oskickat
          </Button>
        </div>
      )}

      {pending && pending.leave.count > 0 && (
        <p className="mt-2 text-sm text-muted-foreground">
          Dessutom väntar <strong>{pending.leave.count} ledighetsdag(ar)</strong> (från{" "}
          {pending.leave.oldest}) — de skickas från fliken Ledighet.
        </p>
      )}

      <p className="mt-4 text-sm text-muted-foreground">
        {shiftRows.length} arbetsdag(ar)
        {leaveCount > 0 ? ` · ${leaveCount} ledighetsdag(ar)` : ""} · totalt arbetat{" "}
        <strong>{formatDuration(totalMinutes)}</strong>. Bara avslutade dagar som inte redan
        skickats tas med i exporten; ledighet skickas från fliken Ledighet.
      </p>

      {missedCount > 0 && (
        <p className="mt-2 flex items-center gap-2 text-sm font-medium text-destructive">
          <TriangleAlert className="size-4" />
          {missedCount === 1
            ? "En dag saknar utstämpling och skickas inte förrän den rättats."
            : `${missedCount} dagar saknar utstämpling och skickas inte förrän de rättats.`}
        </p>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground uppercase">
              <th className="py-2 pr-3 font-medium">Anställd</th>
              <th className="py-2 pr-3 font-medium">Datum</th>
              <th className="py-2 pr-3 font-medium">In</th>
              <th className="py-2 pr-3 font-medium">Lunch</th>
              <th className="py-2 pr-3 font-medium">Ut</th>
              <th className="py-2 pr-3 font-medium">Arbetat</th>
              <th className="py-2 pr-3 font-medium">Kod</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-muted-foreground">
                  Hämtar…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-muted-foreground">
                  Ingen tid registrerad under perioden.
                </td>
              </tr>
            ) : (
              rows.map((row) =>
                row.kind === "leave" ? (
                  <tr key={row.key} className="text-muted-foreground">
                    <td className="py-2.5 pr-3 font-medium text-foreground">
                      {row.employee?.name ?? "Okänd"}
                    </td>
                    <td className="tabular py-2.5 pr-3">{row.date}</td>
                    <td colSpan={3} className="py-2.5 pr-3 italic">
                      {VACATION_LABELS[row.request.kind]}
                    </td>
                    <td className="tabular py-2.5 pr-3">—</td>
                    <td className="py-2.5 pr-3 text-xs">
                      {VACATION_CAUSE_CODES[row.request.kind]}
                    </td>
                    <td className="py-2.5 pr-3">
                      <ExportBadge exportedAt={row.request.exported_at} />
                    </td>
                    <td />
                  </tr>
                ) : (
                  <ShiftTableRow
                    key={row.key}
                    row={row}
                    onEdit={(shift) =>
                      setDraft({
                        shift,
                        check_in: toTimeInput(shift.check_in),
                        lunch_start: toTimeInput(shift.lunch_start),
                        lunch_end: toTimeInput(shift.lunch_end),
                        check_out: toTimeInput(shift.check_out),
                        note: shift.note ?? "",
                      })
                    }
                  />
                ),
              )
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!addDraft} onOpenChange={(open) => !open && setAddDraft(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Lägg till dag</DialogTitle>
          </DialogHeader>

          {addDraft && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="add-employee">Anställd</Label>
                <select
                  id="add-employee"
                  className="h-10 w-full rounded-xl border bg-background px-3 text-sm"
                  value={addDraft.employee_id}
                  onChange={(event) =>
                    setAddDraft({ ...addDraft, employee_id: event.target.value })
                  }
                >
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-date">Datum</Label>
                <Input
                  id="add-date"
                  type="date"
                  value={addDraft.work_date}
                  onChange={(event) => setAddDraft({ ...addDraft, work_date: event.target.value })}
                />
              </div>

              {(
                [
                  ["check_in", "Stämplade in"],
                  ["lunch_start", "Lunch början"],
                  ["lunch_end", "Lunch slut"],
                  ["check_out", "Stämplade ut"],
                ] as const
              ).map(([field, label]) => (
                <div key={field} className="space-y-1.5">
                  <Label htmlFor={`add-${field}`}>{label}</Label>
                  <Input
                    id={`add-${field}`}
                    type="time"
                    value={addDraft[field]}
                    onChange={(event) => setAddDraft({ ...addDraft, [field]: event.target.value })}
                  />
                </div>
              ))}

              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="add-note">Anteckning</Label>
                <Textarea
                  id="add-note"
                  value={addDraft.note}
                  onChange={(event) => setAddDraft({ ...addDraft, note: event.target.value })}
                  placeholder="Varför läggs dagen till i efterhand?"
                />
              </div>

              <p className="col-span-2 text-xs text-muted-foreground">
                Dagen märks som ändrad och följer med nästa export till Fortnox, precis som en
                stämplad dag.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setAddDraft(null)}>
              Avbryt
            </Button>
            <Button
              className="rounded-full"
              disabled={addDay.isPending}
              onClick={() => addDraft && addDay.mutate(addDraft)}
            >
              {addDay.isPending ? "Lägger till…" : "Lägg till"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!draft} onOpenChange={(open) => !open && setDraft(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>
              Rätta tider · {employeeById.get(draft?.shift.employee_id ?? "")?.name} ·{" "}
              {draft?.shift.work_date}
            </DialogTitle>
          </DialogHeader>

          {draft && (
            <div className="grid grid-cols-2 gap-4">
              {(
                [
                  ["check_in", "Stämplade in"],
                  ["lunch_start", "Lunch början"],
                  ["lunch_end", "Lunch slut"],
                  ["check_out", "Stämplade ut"],
                ] as const
              ).map(([field, label]) => (
                <div key={field} className="space-y-1.5">
                  <Label htmlFor={field}>{label}</Label>
                  <Input
                    id={field}
                    type="time"
                    value={draft[field]}
                    onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                  />
                </div>
              ))}
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="note">Anteckning</Label>
                <Textarea
                  id="note"
                  value={draft.note}
                  onChange={(event) => setDraft({ ...draft, note: event.target.value })}
                  placeholder="Anledning till ändringen"
                />
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-between">
            {/* Bara dagar som inte skickats — Fortnox har redan de exporterade. */}
            {draft && !draft.shift.exported_at ? (
              <Button
                variant="ghost"
                className="rounded-full text-destructive hover:text-destructive"
                disabled={removeDay.isPending}
                onClick={() => removeDay.mutate(draft.shift.id)}
              >
                <Trash2 className="size-4" />
                {removeDay.isPending ? "Tar bort…" : "Ta bort dagen"}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" className="rounded-full" onClick={() => setDraft(null)}>
                Avbryt
              </Button>
              <Button
                className="rounded-full"
                disabled={saveEdit.isPending}
                onClick={() => draft && saveEdit.mutate(draft)}
              >
                {saveEdit.isPending ? "Sparar…" : "Spara"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const BADGE_TONES = {
  success: "bg-success/15 text-success",
  warning: "bg-warning/20 text-warning-foreground",
  destructive: "bg-destructive/12 text-destructive",
  muted: "bg-muted text-muted-foreground",
} as const;

function Badge({ tone, children }: { tone: keyof typeof BADGE_TONES; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        BADGE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * Skickad eller inte — det är den enda status som spelar roll för lönen, så den
 * ska gå att se på en halv sekund. Grön med datum när den gått iväg, gul när den
 * väntar. Att dagen rättats för hand är en detalj som visas inuti.
 */
function ExportBadge({ exportedAt, edited }: { exportedAt: string | null; edited?: boolean }) {
  if (exportedAt) {
    return (
      <Badge tone="success">
        <Check className="size-3" />
        Skickad {exportedAt.slice(0, 10)}
      </Badge>
    );
  }
  return <Badge tone="warning">Ej skickad{edited ? " · ändrad" : ""}</Badge>;
}

function ShiftTableRow({ row, onEdit }: { row: ShiftRow; onEdit: (shift: Shift) => void }) {
  const { shift, employee, worked, lunch, missed } = row;

  return (
    <tr className={cn(missed && "bg-destructive/5")}>
      <td className="py-2.5 pr-3 font-medium">{employee?.name ?? "Okänd"}</td>
      <td className="tabular py-2.5 pr-3">{shift.work_date}</td>
      <td className="tabular py-2.5 pr-3">{formatClock(shift.check_in)}</td>
      <td className="tabular py-2.5 pr-3">{lunch ? `${lunch} min` : "—"}</td>
      <td className="tabular py-2.5 pr-3">{formatClock(shift.check_out)}</td>
      <td className="tabular py-2.5 pr-3">{missed ? "—" : `${decimalHours(worked)} h`}</td>
      <td
        className="py-2.5 pr-3 text-xs text-muted-foreground"
        title={
          employee
            ? (ATTENDANCE_CAUSE_LABELS[employee.attendance_code as AttendanceCauseCode] ??
              undefined)
            : undefined
        }
      >
        {employee?.attendance_code ?? "—"}
      </td>
      <td className="py-2.5 pr-3">
        {missed ? (
          <Badge tone="destructive">Ej utstämplad</Badge>
        ) : !shift.check_out ? (
          <Badge tone="muted">Pågår</Badge>
        ) : (
          <ExportBadge exportedAt={shift.exported_at} edited={shift.manually_edited} />
        )}
      </td>
      <td className="py-2.5 text-right">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label="Rätta tider"
          onClick={() => onEdit(shift)}
        >
          <Pencil className="size-4" />
        </Button>
      </td>
    </tr>
  );
}
